<script lang="ts">
    import {userContainer} from "$lib/stores/stores.svelte"
    import { onDestroy, onMount } from "svelte";
    import {ArrowRight, ArrowDown} from "@lucide/svelte";
    import {m} from "$lib/paraglide/messages";
    import { localizeHref } from "$lib/paraglide/runtime";
    import { isViewTransitioning } from "$lib/stores/viewTransition.svelte";

let imgVisible = $state(isViewTransitioning);
let revealed = $state<Set<string>>(new Set());

    onMount(() => {
        userContainer.prevPage = null;

        if (!imgVisible) {
            setTimeout(() => {
                imgVisible = true;
            }, 50);
        }

        const observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                const id = (entry.target as HTMLElement).dataset.reveal!;
                revealed = new Set([...revealed, id]);
                observer.unobserve(entry.target);
              }
            }
          },
          { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
        );
        document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    });

    onDestroy(() => {
        userContainer.prevPage = '';
    });

    function isRevealed(id: string): boolean {
      return revealed.has(id);
    }
</script>

<svelte:head>
  <title>Healthy Shoes Cyprus | kybun - Walk on Air</title>
  <meta name="description" content="Discover pain-free walking with kybun shoes in Cyprus. Official partner offering premium Swiss orthopedic footwear for ultimate comfort and health." />
  <meta property="og:type" content="website" />
  <meta property="og:title" content="Healthy Shoes Cyprus | kybun - Walk on Air" />
  <meta property="og:description" content="Discover pain-free walking with kybun shoes in Cyprus. Official partner offering premium Swiss orthopedic footwear." />
  <meta property="og:image" content="https://healthyshoes.com.cy/kybun1.jpg" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Healthy Shoes Cyprus | kybun - Walk on Air" />
  <meta name="twitter:description" content="Discover pain-free walking with kybun shoes in Cyprus. Official partner offering premium Swiss orthopedic footwear." />
  <meta name="twitter:image" content="https://healthyshoes.com.cy/kybun1.jpg" />
</svelte:head>

<div class="landing-hero bg-primary text-white">
    <!-- DESKTOP LAYOUT (lg+) -->
    <div class="desktop-only">
        <div class="desktop-content">
            <a
                href={localizeHref("/shoes")}
                class="desktop-shoe-link transition-opacity duration-300 {imgVisible ? 'opacity-100' : 'opacity-0'}"
                aria-label="rolle white"
            >
                <enhanced:img
                    src="/static/AM216A_Rolle_white_a.png"
                    alt="A stylish modern shoe appearing to fall or float"
                    class="shoe-img"
                />
            </a>

            <div class="desktop-text">
                <h1 class="text-7xl xl:text-8xl 2xl:text-9xl font-extrabold leading-none tracking-tighter">
                    <span class="block transition-transform duration-300 ease-out hover:translate-x-1 rtl:hover:-translate-x-1">{m.landing1()}</span>
                    <span class="block ms-8 transition-transform duration-300 ease-out hover:translate-x-1 rtl:hover:-translate-x-1 delay-75">{m.landing2()}</span>
                    <span class="block ms-16 transition-transform duration-300 ease-out hover:translate-x-1 rtl:hover:-translate-x-1 delay-150">{m.landing3()}</span>
                </h1>
                <a href="#technology" class="flex items-center mt-6 text-xl xl:text-2xl 2xl:text-3xl font-semibold hover:underline">
                    {m.landing_caption()}
                    <ArrowRight class="ms-2 size-6 rtl:-scale-x-100" />
                </a>
            </div>
        </div>
    </div>

    <!-- MOBILE LAYOUT (<lg) -->
    <div class="mobile-only">
        <div class="mobile-row">
            <a
                href={localizeHref("/shoes")}
                class="mobile-shoe-link transition-opacity duration-300 {imgVisible ? 'opacity-100' : 'opacity-0'}"
                aria-label="rolle white"
            >
                <enhanced:img
                    src="/static/AM216A_Rolle_white_a.png"
                    alt="A stylish modern shoe appearing to fall or float"
                    class="shoe-img-mobile"
                />
            </a>

            <div class="mobile-text">
                <h1 class="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-none tracking-tighter">
                    <span class="block transition-transform duration-300 ease-out hover:translate-x-1 rtl:hover:-translate-x-1">{m.landing1()}</span>
                    <span class="block ms-4 sm:ms-6 transition-transform duration-300 ease-out hover:translate-x-1 rtl:hover:-translate-x-1 delay-75">{m.landing2()}</span>
                    <span class="block ms-8 sm:ms-12 transition-transform duration-300 ease-out hover:translate-x-1 rtl:hover:-translate-x-1 delay-150">{m.landing3()}</span>
                </h1>
            </div>
        </div>

        <a href="#technology" class="mobile-caption">
            {m.landing_caption()}
            <ArrowDown class="ms-2 size-5" />
        </a>
    </div>
</div>

<!-- ===================== TECHNOLOGY SECTIONS ===================== -->

<section class="s-problem" id="technology" data-reveal="problem">
  <div class="wide">
    <div class="problem-inner" class:show={isRevealed('problem')}>
      <p class="kicker">{m.tech_problem_kicker()}</p>
      <h2 class="display">
        {m.tech_problem_heading1()}<br />{m.tech_problem_heading2()}
      </h2>
      <div class="problem-body">
        <p>{m.tech_problem_body1()}</p>
        <p>{m.tech_problem_body2()}</p>
      </div>
    </div>
  </div>
</section>

<section class="s-discovery" data-reveal="discovery">
  <div class="wide">
    <div class="discovery-layout" class:show={isRevealed('discovery')}>
      <div class="discovery-text">
        <p class="kicker">{m.tech_discovery_kicker()}</p>
        <h2 class="display">
          {m.tech_discovery_heading()}
        </h2>
        <div class="discovery-body">
          <p>{m.tech_discovery_body1()}</p>
          <p>{m.tech_discovery_body2()}</p>
        </div>
      </div>
      <div class="discovery-portrait">
        <img src="/karl_muller.webp" alt="Karl Muller, inventor of kybun technology" class="portrait-img" />
        <span class="portrait-caption">{m.tech_discovery_caption()}</span>
      </div>
    </div>
  </div>
</section>

<section class="s-tech-image" data-reveal="techimg">
  <div class="tech-image-wrap" class:show={isRevealed('techimg')}>
    <enhanced:img
      src="/static/kybun2.jpg"
      alt="People wearing kybun boots on a nature walk"
      class="tech-img"
    />
  </div>
</section>

<section class="s-mechanism" data-reveal="mech">
  <div class="wide">
    <div class="mech-inner" class:show={isRevealed('mech')}>
      <p class="kicker">{m.tech_mech_kicker()}</p>
      <h2 class="display">{m.tech_mech_heading()}</h2>

      <a href={localizeHref('/shoes/airolo-moon-rock-men-shoes')} class="mech-shoe-showcase">
        <img src="/airolo_moonrock.webp" alt="Airolo Moon Rock" class="mech-shoe-img" />
        <div class="mech-shoe-shadow"></div>
        <span class="mech-shoe-caption">Airolo Moon Rock</span>
      </a>

      <div class="mech-grid">
        <div class="mech-item" style="--delay: 0ms">
          <span class="mech-num">01</span>
          <div>
            <h3 class="mech-title">{m.tech_mech1_title()}</h3>
            <p class="mech-desc">{m.tech_mech1_desc()}</p>
          </div>
        </div>
        <div class="mech-item" style="--delay: 120ms">
          <span class="mech-num">02</span>
          <div>
            <h3 class="mech-title">{m.tech_mech2_title()}</h3>
            <p class="mech-desc">{m.tech_mech2_desc()}</p>
          </div>
        </div>
        <div class="mech-item" style="--delay: 240ms">
          <span class="mech-num">03</span>
          <div>
            <h3 class="mech-title">{m.tech_mech3_title()}</h3>
            <p class="mech-desc">{m.tech_mech3_desc()}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="s-stat" data-reveal="stat">
  <div class="stat-inner" class:show={isRevealed('stat')}>
    <div class="stat-item" style="--delay: 0ms">
      <span class="stat-number">50%</span>
      <span class="stat-label">{m.tech_stat1_label()}</span>
    </div>
    <div class="stat-divider"></div>
    <div class="stat-item" style="--delay: 150ms">
      <span class="stat-number">20+</span>
      <span class="stat-label">{m.tech_stat2_label()}</span>
    </div>
    <div class="stat-divider"></div>
    <div class="stat-item" style="--delay: 300ms">
      <span class="stat-number">100%</span>
      <span class="stat-label">{m.tech_stat3_label()}</span>
    </div>
  </div>
</section>

<section class="s-benefits" data-reveal="benefits">
  <div class="wide">
    <div class="benefits-inner" class:show={isRevealed('benefits')}>
      <p class="kicker">{m.tech_benefits_kicker()}</p>
      <h2 class="display">{m.tech_benefits_heading()}</h2>

      <div class="benefits-list">
        <div class="benefit-row" style="--delay: 0ms">
          <h3 class="benefit-name">{m.tech_benefit1_name()}</h3>
          <p class="benefit-detail">{m.tech_benefit1_detail()}</p>
        </div>
        <div class="benefit-row" style="--delay: 80ms">
          <h3 class="benefit-name">{m.tech_benefit2_name()}</h3>
          <p class="benefit-detail">{m.tech_benefit2_detail()}</p>
        </div>
        <div class="benefit-row" style="--delay: 160ms">
          <h3 class="benefit-name">{m.tech_benefit3_name()}</h3>
          <p class="benefit-detail">{m.tech_benefit3_detail()}</p>
        </div>
        <div class="benefit-row" style="--delay: 240ms">
          <h3 class="benefit-name">{m.tech_benefit4_name()}</h3>
          <p class="benefit-detail">{m.tech_benefit4_detail()}</p>
        </div>
        <div class="benefit-row" style="--delay: 320ms">
          <h3 class="benefit-name">{m.tech_benefit5_name()}</h3>
          <p class="benefit-detail">{m.tech_benefit5_detail()}</p>
        </div>
        <div class="benefit-row" style="--delay: 400ms">
          <h3 class="benefit-name">{m.tech_benefit6_name()}</h3>
          <p class="benefit-detail">{m.tech_benefit6_detail()}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="s-quote" data-reveal="quote">
  <div class="wide">
    <div class="quote-inner" class:show={isRevealed('quote')}>
      <blockquote class="expert-quote">
        "{m.tech_quote_text()}"
      </blockquote>
      <div class="expert-attr">
        <img src="/expert_gaebler.webp" alt="Prof. Dr. Christian Gäbler" class="expert-photo" />
        <div>
          <span class="expert-name">Prof. Dr. Christian Gäbler</span>
          <span class="expert-title">{m.tech_quote_title()}</span>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="s-swiss" data-reveal="swiss">
  <div class="swiss-image-strip">
    <img src="/karl_sennwald.webp" alt="Karl Muller in the kybun production facility in Sennwald, Switzerland" class="swiss-strip-img" />
  </div>
  <div class="wide">
    <div class="swiss-inner" class:show={isRevealed('swiss')}>
      <div class="swiss-text">
        <p class="kicker">{m.tech_swiss_kicker()}</p>
        <h2 class="display">
          {m.tech_swiss_heading1()}<br />{m.tech_swiss_heading2()}
        </h2>
        <p class="swiss-body">{m.tech_swiss_body()}</p>
      </div>
      <div class="swiss-logo-area">
        <div class="swiss-badge-row">
          <img src="/icon_swiss.svg" alt="Made in Switzerland" class="swiss-badge-icon" />
          <img src="/Kybun-LOGO_vektor_rot.eps.svg" alt="kybun" class="swiss-logo" />
        </div>
        <div class="swiss-certs">
          <a href="/agr-certification.pdf" target="_blank" rel="noopener" class="cert-link">{m.tech_swiss_agr()}</a>
          <span class="cert-dot"></span>
          <a href="https://www.kybun.com/fileadmin/user_upload/documents/ETH-Studie/ETH-Studie_cs4_EN.pdf" target="_blank" rel="noopener" class="cert-link">{m.tech_swiss_eth()}</a>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="s-products" data-reveal="products">
  <div class="wide">
    <div class="products-inner" class:show={isRevealed('products')}>
      <p class="kicker">{m.tech_products_kicker()}</p>
      <h2 class="display">{m.tech_products_heading()}</h2>

      <div class="products-row">
        <a href={localizeHref('/shoes')} class="prod-link">
          <img src="/airolo_moonrock.webp" alt="" class="prod-thumb" />
          <span class="prod-name">{m.shoes()}</span>
          <span class="prod-desc">{m.tech_shoes_desc()}</span>
          <span class="prod-arrow">
            <ArrowRight size={20} color="currentColor" class="rtl:-scale-x-100" />
          </span>
        </a>
        <a href={localizeHref('/mats')} class="prod-link">
          <img src="/kybun_mat.webp" alt="" class="prod-thumb" />
          <span class="prod-name">{m.mats()}</span>
          <span class="prod-desc">{m.tech_mats_desc()}</span>
          <span class="prod-arrow">
            <ArrowRight size={20} color="currentColor" class="rtl:-scale-x-100" />
          </span>
        </a>
      </div>
    </div>
  </div>
</section>

<section class="s-cta" data-reveal="cta">
  <div class="cta-inner" class:show={isRevealed('cta')}>
    <h2 class="cta-title">{m.tech_cta_title()}</h2>
    <p class="cta-sub">{m.tech_cta_sub()}</p>
    <div class="cta-actions">
      <a href={localizeHref('/shoes')} class="btn-primary">
        {m.tech_cta_shop()}
        <ArrowRight size={18} color="currentColor" class="rtl:-scale-x-100" />
      </a>
      <a href={localizeHref('/find-us')} class="btn-ghost">
        {m.tech_cta_visit()}
        <ArrowRight size={18} color="currentColor" class="rtl:-scale-x-100" />
      </a>
    </div>
  </div>
</section>

<style>
    /* ===== LANDING HERO ===== */
    .landing-hero {
        height: calc(100dvh - 11rem);
        overflow: hidden;
    }

    .desktop-only {
        display: none;
    }

    @media (min-width: 1024px) {
        .desktop-only {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .desktop-content {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0;
        }

        .desktop-shoe-link {
            display: block;
        }

        .shoe-img {
            transform: rotate(45deg);
            height: 32vh;
            width: auto;
            object-fit: contain;
            filter: drop-shadow(0 25px 25px rgb(0 0 0 / 0.15));
            transition: transform 0.5s ease-in-out;
        }

        .shoe-img:hover {
            transform: rotate(45deg) scale(1.05);
        }

        /* RTL: the hero mirrors, so mirror the shoe too and keep it pointing away from the headline. */
        :global([dir="rtl"]) .shoe-img {
            transform: scaleX(-1) rotate(45deg);
        }

        :global([dir="rtl"]) .shoe-img:hover {
            transform: scaleX(-1) rotate(45deg) scale(1.05);
        }

        .desktop-text {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }
    }

    .mobile-only {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 1rem;
    }

    .mobile-row {
        display: flex;
        flex-direction: row;
        align-items: center;
    }

    .mobile-shoe-link {
        display: block;
    }

    .shoe-img-mobile {
        transform: rotate(45deg);
        height: 16vh;
        width: auto;
        object-fit: contain;
        filter: drop-shadow(0 25px 25px rgb(0 0 0 / 0.15));
        transition: transform 0.5s ease-in-out;
    }

    .shoe-img-mobile:hover {
        transform: rotate(45deg) scale(1.05);
    }

    :global([dir="rtl"]) .shoe-img-mobile {
        transform: scaleX(-1) rotate(45deg);
    }

    :global([dir="rtl"]) .shoe-img-mobile:hover {
        transform: scaleX(-1) rotate(45deg) scale(1.05);
    }

    .mobile-text {
        display: flex;
        align-items: center;
        margin-inline-start: -3rem;
    }

    .mobile-caption {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin-top: 4.5rem;
        padding: 0 1.5rem;
        font-size: 1.125rem;
        font-weight: 600;
        line-height: 1.4;
        text-align: start;
    }

    .mobile-caption:hover {
        text-decoration: underline;
    }

    @media (min-width: 640px) {
        .mobile-caption {
            font-size: 1.25rem;
            padding: 0 2rem;
        }
    }

    @media (min-width: 1024px) {
        .mobile-only {
            display: none;
        }
    }

    /* ===== TECH SECTIONS: SHARED ===== */
    .wide {
      max-width: 72rem;
      margin: 0 auto;
      padding: 0 1.5rem;
    }
    @media (min-width: 960px) {
      .wide { padding: 0 3rem; }
    }

    .kicker {
      font-size: 0.8125rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      opacity: 0.5;
      margin-bottom: 1.5rem;
    }

    .display {
      font-size: clamp(1.75rem, 5vw, 3.25rem);
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: -0.025em;
    }

    /* ===== REVEAL ANIMATIONS ===== */
    [data-reveal] :is(.problem-inner, .discovery-layout, .mech-inner, .stat-inner,
      .benefits-inner, .quote-inner, .swiss-inner, .products-inner, .cta-inner, .tech-image-wrap) {
      opacity: 0;
      transform: translateY(40px);
      transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                  transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }
    :is(.problem-inner, .discovery-layout, .mech-inner, .stat-inner,
      .benefits-inner, .quote-inner, .swiss-inner, .products-inner, .cta-inner, .tech-image-wrap).show {
      opacity: 1;
      transform: translateY(0);
    }

    .mech-item, .stat-item, .stat-divider, .benefit-row {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                  transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
      transition-delay: var(--delay, 0ms);
    }
    .show .mech-item, .show .stat-item, .show .stat-divider, .show .benefit-row {
      opacity: 1;
      transform: translateY(0);
    }

    /* ===== PROBLEM ===== */
    .s-problem {
      padding: 6rem 0;
      color: var(--color-primary);
    }
    :global(.dark) .s-problem { color: white; }
    .s-problem .display { max-width: 720px; }

    .problem-body {
      margin-top: 2.5rem;
      max-width: 600px;
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }
    .problem-body p {
      font-size: 1.0625rem;
      line-height: 1.8;
      opacity: 0.65;
    }

    @media (min-width: 960px) {
      .s-problem { padding: 8rem 0; }
      .problem-body p { font-size: 1.125rem; }
    }

    /* ===== DISCOVERY ===== */
    .s-discovery {
      padding: 6rem 0;
      background: var(--color-primary);
      color: white;
    }
    .s-discovery .kicker { opacity: 0.4; }

    .discovery-layout {
      display: flex;
      flex-direction: column;
      gap: 3rem;
    }
    .discovery-text .display { max-width: 720px; }

    .discovery-body {
      margin-top: 2rem;
      max-width: 580px;
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }
    .discovery-body p {
      font-size: 1.0625rem;
      line-height: 1.8;
      opacity: 0.6;
    }

    .discovery-portrait {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      align-self: flex-start;
    }
    .portrait-img {
      width: 200px;
      height: auto;
      border-radius: 0.75rem;
      opacity: 0.85;
    }
    .portrait-caption {
      font-size: 0.8125rem;
      opacity: 0.4;
      font-weight: 500;
    }

    @media (min-width: 960px) {
      .s-discovery { padding: 8rem 0; }
      .discovery-layout {
        flex-direction: row;
        align-items: flex-start;
        gap: 4rem;
      }
      .discovery-text { flex: 1; }
      .discovery-portrait { align-self: flex-start; flex-shrink: 0; }
      .portrait-img { width: 260px; }
      .discovery-body p { font-size: 1.125rem; }
    }

    /* ===== TECH IMAGE ===== */
    .s-tech-image {
      background: var(--color-primary);
      padding: 0 1.5rem 1.5rem;
    }
    @media (min-width: 960px) {
      .s-tech-image { padding: 0 3rem 3rem; }
    }
    .tech-image-wrap {
      max-width: 72rem;
      margin: 0 auto;
      border-radius: 1rem;
      overflow: hidden;
    }
    .tech-img {
      width: 100%;
      height: auto;
      display: block;
    }

    /* ===== MECHANISM ===== */
    .s-mechanism {
      padding: 6rem 0;
      color: var(--color-primary);
    }
    :global(.dark) .s-mechanism { color: white; }
    .s-mechanism .display { margin-bottom: 1.5rem; }

    .mech-shoe-showcase {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 3rem;
      text-decoration: none;
      color: inherit;
    }
    .mech-shoe-img {
      width: 240px;
      height: auto;
      mix-blend-mode: multiply;
      animation: shoe-float 3s ease-in-out infinite;
    }
    :global(.dark) .mech-shoe-img {
      mix-blend-mode: normal;
      border-radius: 1.25rem;
    }
    .mech-shoe-shadow {
      width: 160px;
      height: 12px;
      border-radius: 50%;
      background: radial-gradient(ellipse, color-mix(in srgb, currentColor 10%, transparent) 0%, transparent 70%);
      animation: shadow-pulse 3s ease-in-out infinite;
      margin-top: -4px;
    }
    @keyframes shoe-float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-18px); }
    }
    @keyframes shadow-pulse {
      0%, 100% { transform: scaleX(1); opacity: 1; }
      50% { transform: scaleX(0.6); opacity: 0.3; }
    }
    .mech-shoe-caption {
      font-size: 0.8125rem;
      font-weight: 500;
      opacity: 0.4;
      margin-top: 0.5rem;
      transition: opacity 0.2s ease;
    }
    .mech-shoe-showcase:hover .mech-shoe-caption {
      opacity: 0.7;
    }
    @media (prefers-reduced-motion: reduce) {
      .mech-shoe-img, .mech-shoe-shadow { animation: none; }
    }

    .mech-grid {
      display: flex;
      flex-direction: column;
    }
    .mech-item {
      display: flex;
      gap: 1.5rem;
      align-items: baseline;
      padding: 2rem 0;
      border-top: 1px solid color-mix(in srgb, currentColor 12%, transparent);
    }
    .mech-item:last-child {
      border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent);
    }
    .mech-num {
      font-size: 0.8125rem;
      font-weight: 600;
      opacity: 0.3;
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
    }
    .mech-title {
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }
    .mech-desc {
      font-size: 1rem;
      line-height: 1.65;
      opacity: 0.55;
      max-width: 520px;
    }

    @media (min-width: 960px) {
      .s-mechanism { padding: 8rem 0; }
      .mech-shoe-img { width: 320px; }
      .mech-shoe-shadow { width: 220px; }
      .mech-item { padding: 2.5rem 0; gap: 2.5rem; }
      .mech-title { font-size: 1.375rem; }
      .mech-desc { font-size: 1.0625rem; }
    }

    /* ===== STATS ===== */
    .s-stat {
      padding: 5rem 1.5rem;
      background: var(--color-primary);
      color: white;
    }
    .stat-inner {
      max-width: 72rem;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2rem;
    }
    .stat-divider {
      width: 40px;
      height: 1px;
      background: rgba(255, 255, 255, 0.2);
    }
    .stat-item { text-align: center; }
    .stat-number {
      display: block;
      font-size: clamp(3rem, 8vw, 4.5rem);
      font-weight: 800;
      letter-spacing: -0.03em;
      line-height: 1;
    }
    .stat-label {
      display: block;
      margin-top: 0.5rem;
      font-size: 0.9375rem;
      opacity: 0.45;
      font-weight: 500;
    }

    @media (min-width: 960px) {
      .s-stat { padding: 6rem 3rem; }
      .stat-inner {
        flex-direction: row;
        justify-content: center;
        gap: 4rem;
      }
      .stat-divider { width: 1px; height: 60px; }
    }

    /* ===== BENEFITS ===== */
    .s-benefits {
      padding: 6rem 0;
      color: var(--color-primary);
    }
    :global(.dark) .s-benefits { color: white; }
    .s-benefits .display { margin-bottom: 3rem; }

    .benefits-list {
      display: flex;
      flex-direction: column;
    }
    .benefit-row {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding: 1.5rem 0;
      border-top: 1px solid color-mix(in srgb, currentColor 12%, transparent);
    }
    .benefit-row:last-child {
      border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent);
    }
    .benefit-name {
      font-size: 1.125rem;
      font-weight: 700;
    }
    .benefit-detail {
      font-size: 1rem;
      opacity: 0.5;
      line-height: 1.5;
    }

    @media (min-width: 960px) {
      .s-benefits { padding: 8rem 0; }
      .benefit-row {
        flex-direction: row;
        align-items: baseline;
        gap: 3rem;
        padding: 1.75rem 0;
      }
      .benefit-name { flex: 0 0 200px; font-size: 1.25rem; }
      .benefit-detail { font-size: 1.0625rem; }
    }

    /* ===== EXPERT QUOTE ===== */
    .s-quote {
      padding: 5rem 0;
      background: var(--color-primary);
      color: white;
    }
    .expert-quote {
      font-size: clamp(1.25rem, 3.5vw, 2rem);
      font-weight: 500;
      line-height: 1.4;
      letter-spacing: -0.015em;
      opacity: 0.85;
      max-width: 680px;
      margin: 0;
      padding: 0;
      border: 0;
    }
    .expert-attr {
      margin-top: 2rem;
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .expert-photo {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
      object-position: center top;
      opacity: 0.75;
    }
    .expert-name {
      display: block;
      font-size: 0.9375rem;
      font-weight: 600;
      opacity: 0.8;
    }
    .expert-title {
      display: block;
      font-size: 0.8125rem;
      opacity: 0.4;
      margin-top: 0.125rem;
    }

    @media (min-width: 960px) {
      .s-quote { padding: 6rem 0; }
    }

    /* ===== SWISS ENGINEERING ===== */
    .s-swiss {
      padding: 0 0 6rem;
      color: var(--color-primary);
    }
    :global(.dark) .s-swiss { color: white; }
    .swiss-image-strip {
      width: 100%;
      margin-bottom: 3rem;
      overflow: hidden;
      max-height: 360px;
    }
    .swiss-strip-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center 30%;
      display: block;
    }
    .swiss-inner {
      display: flex;
      flex-direction: column;
      gap: 2.5rem;
    }
    .swiss-text .display { margin-bottom: 1.5rem; }
    .swiss-body {
      font-size: 1.0625rem;
      line-height: 1.8;
      opacity: 0.6;
      max-width: 580px;
    }
    .swiss-logo-area {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    .swiss-badge-row {
      display: flex;
      align-items: center;
      gap: 1.5rem;
    }
    .swiss-badge-icon {
      height: 3rem;
      width: auto;
    }
    :global(.dark) .swiss-badge-icon {
      filter: brightness(0) invert(1);
    }
    .swiss-logo {
      height: 1.75rem;
      width: auto;
      opacity: 0.6;
    }
    :global(.dark) .swiss-logo {
      filter: brightness(0) invert(1);
      opacity: 0.5;
    }
    .swiss-certs {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.8125rem;
      font-weight: 500;
      opacity: 0.4;
    }
    .cert-link {
      color: inherit;
      text-decoration: none;
      transition: opacity 0.2s ease;
    }
    .cert-link:hover {
      opacity: 0.8;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .cert-dot {
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background: currentColor;
    }

    @media (min-width: 960px) {
      .s-swiss { padding: 0 0 8rem; }
      .swiss-image-strip { max-height: 440px; }
      .swiss-inner {
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
        gap: 4rem;
      }
      .swiss-body { font-size: 1.125rem; }
      .swiss-logo-area { align-items: flex-end; flex-shrink: 0; }
      .swiss-logo { height: 2rem; }
      .swiss-badge-icon { height: 3.5rem; }
    }

    /* ===== PRODUCTS ===== */
    .s-products {
      padding: 6rem 0;
      background: var(--color-primary);
      color: white;
    }
    .s-products .kicker { opacity: 0.4; }
    .s-products .display { margin-bottom: 3rem; }
    .products-row {
      display: flex;
      flex-direction: column;
    }
    .prod-link {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      padding: 2rem 0;
      border-top: 1px solid rgba(255, 255, 255, 0.12);
      text-decoration: none;
      color: inherit;
      transition: opacity 0.2s ease;
    }
    .prod-link:last-child {
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    }
    .prod-link:hover { opacity: 0.6; }
    .prod-thumb {
      width: 64px;
      height: 64px;
      object-fit: contain;
      border-radius: 0.75rem;
      background: white;
      flex-shrink: 0;
    }
    .prod-name {
      font-size: 1.5rem;
      font-weight: 700;
      flex-shrink: 0;
    }
    .prod-desc {
      flex: 1;
      font-size: 1rem;
      opacity: 0.45;
      display: none;
    }
    .prod-arrow {
      opacity: 0.3;
      margin-inline-start: auto;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }
    .prod-link:hover .prod-arrow {
      transform: translateX(4px);
      opacity: 0.7;
    }
    :global([dir="rtl"]) .prod-link:hover .prod-arrow {
      transform: translateX(-4px);
    }

    @media (min-width: 960px) {
      .s-products { padding: 8rem 0; }
      .prod-thumb { width: 80px; height: 80px; }
      .prod-name { font-size: 2rem; min-width: 140px; }
      .prod-desc { display: block; font-size: 1.0625rem; }
      .prod-link { gap: 3rem; padding: 2.5rem 0; }
    }

    /* ===== CTA ===== */
    .s-cta {
      padding: 6rem 1.5rem;
      background: var(--color-primary);
      color: white;
      text-align: center;
    }
    .cta-title {
      font-size: clamp(2.5rem, 7vw, 4.5rem);
      font-weight: 800;
      letter-spacing: -0.03em;
      line-height: 1;
      margin-bottom: 1rem;
    }
    .cta-sub {
      font-size: 1.0625rem;
      opacity: 0.5;
      max-width: 400px;
      margin: 0 auto 2.5rem;
      line-height: 1.6;
    }
    .cta-actions {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      align-items: center;
    }
    .btn-primary, .btn-ghost {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.875rem 2.25rem;
      font-size: 0.9375rem;
      font-weight: 600;
      border-radius: 100px;
      transition: transform 0.15s ease, background-color 0.2s ease, border-color 0.2s ease;
      min-width: 200px;
      justify-content: center;
    }
    .btn-primary {
      background: white;
      color: var(--color-primary);
    }
    .btn-primary:hover {
      transform: scale(1.03);
      background: rgba(255, 255, 255, 0.92);
    }
    .btn-ghost {
      color: white;
      border: 1.5px solid rgba(255, 255, 255, 0.25);
    }
    .btn-ghost:hover {
      transform: scale(1.03);
      border-color: rgba(255, 255, 255, 0.5);
    }
    .btn-primary:active, .btn-ghost:active {
      transform: scale(0.98);
    }

    @media (min-width: 640px) {
      .cta-actions { flex-direction: row; justify-content: center; }
    }
    @media (min-width: 960px) {
      .s-cta { padding: 8rem 3rem; }
      .cta-sub { font-size: 1.125rem; }
    }
</style>
